"use client";
import * as React from "react";
import { gsap } from "gsap";

type InputProps =
  | ({ as?: "input" } & React.InputHTMLAttributes<HTMLInputElement>)
  | ({ as: "textarea" } & React.TextareaHTMLAttributes<HTMLTextAreaElement>);

const Input = React.forwardRef<HTMLInputElement & HTMLTextAreaElement, InputProps>(
  ({ className = "", id, as = "input", ...props }, ref) => {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const radius = 80;

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      const el = containerRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      gsap.to(el, {
        background: `radial-gradient(${radius}px circle at ${x}px ${y}px, #f0c869, transparent 70%)`,
        duration: 0.3,
        overwrite: "auto",
      });
    };

    const handleMouseLeave = () => {
      const el = containerRef.current;
      if (!el) return;

      gsap.to(el, {
        background: "transparent",
        duration: 0.3,
        overwrite: "auto",
      });
    };

    const baseClass =
      "p-3 rounded-md border border-[#2a2a38] bg-[#1a1a23] text-gray-100 text-base " +
      "focus:outline-none w-full";

    return (
      <div
        ref={containerRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className={`rounded-lg p-[2px] overflow-hidden transition duration-300 ${className}`}
      >
        {as === "textarea" ? (
          <textarea
            ref={ref as React.Ref<HTMLTextAreaElement>}
            {...(props as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
            id={id}
            name={id}
            aria-label={`Enter your ${id}`}
            className={baseClass}
          />
        ) : (
          <input
            ref={ref as React.Ref<HTMLInputElement>}
            id={id}
            name={id}
            autoComplete={id}
            aria-label={`Enter your ${id}`}
            {...(props as React.InputHTMLAttributes<HTMLInputElement>)}
            className={baseClass}
          />
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export { Input };
